import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TravelBlockPassengerForm } from "Bands/components";
import {
  dataMetaSelector,
  createTravelBlockPassenger,
  dispatchGetTravelBlockPassengers,
} from "Bands/state";
import {
  BAND_ACTIONS,
  EventTravelLegType,
  ITravelBlockPassengerParams,
} from "Bands/types";
import { addTemporaryToast } from "shared/components/Toast";
import {
  IAPIState,
  IStatus,
  IFormButtons,
  ICardTheme,
  IJSONAPIErrorCodes,
} from "shared/types";
import { useAPIError, useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { Card, Title } from "shared/components";
import { Spacer } from "shared/layout";
import { useBandContext } from "Bands/contexts";

const createCopy = copy.travelBlock.passenger.create;

interface Props {
  blockId: string;
  travelLegs: EventTravelLegType[];
  handleClose: () => void;
}

export const CreateTravelBlockPassengerForm: FC<Props> = ({
  blockId,
  travelLegs,
  handleClose,
}) => {
  const [resetForm, setResetForm] = useState(false);
  const dispatch = useDispatch();
  const { bandId } = useBandContext();

  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createTravelBlockPassenger)
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.createTravelBlockPassenger);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      setResetForm(true);

      dispatchGetTravelBlockPassengers({
        dispatch,
        blockId,
      });

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: createCopy.messages.success,
        })
      );
    }
  }, [apiMeta?.status, blockId, dispatch]);

  const handleSubmit = (data: ITravelBlockPassengerParams) => {
    setResetForm(false);
    dispatch(createTravelBlockPassenger(data));
  };

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        children: createCopy.buttons.create,
        "data-testid": "create_travel_block_passenger",
        size: "m",
      },
      secondary: {
        onClick: handleClose,
        size: "m",
      },
    }),
    [handleClose]
  );

  const { error: passengerAlreadyAddedError } = useAPIError({
    apiError: apiMeta?.error,
    message: createCopy.messages.alreadyAdded,
    errorCode: IJSONAPIErrorCodes.NotUniqueError,
  });

  return (
    <Card theme={ICardTheme.secondary} size="m">
      <Spacer size="l">
        <Title size="s" asComponent="h3">
          {createCopy.title}
        </Title>
        <TravelBlockPassengerForm
          bandId={bandId || ""}
          resetForm={resetForm}
          blockId={blockId}
          handleSubmit={handleSubmit}
          buttons={buttons}
          travelLegs={travelLegs}
          apiStatus={apiMeta?.status}
          apiError={apiMeta?.error}
          error={passengerAlreadyAddedError}
        />
      </Spacer>
    </Card>
  );
};
